import { boolean, number, object, ObjectSchema, string } from 'yup';

import { RemitClaim } from '@/types/billing';
import { transformEmptyStringToNull } from '@/utils/transformEmptyStringToNull';

const UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const RemitClaimSchema: ObjectSchema<
  Omit<RemitClaim, 'remit' | 'service_lines' | 'financial_info' | 'id' | 'person'>
> = object().shape({
  patient_control_number: string().trim().max(100).required().transform(transformEmptyStringToNull),
  remit: number().nullable(),
  remit_id: number().nullable(),
  person_id: string().nullable().matches(UUID_REGEX, 'Must be a valid UUID'),
  healthie_cms_1500: string().nullable().matches(UUID_REGEX, 'Must be a valid UUID'),
  payer_classification: string()
    .oneOf(['primary', 'secondary', 'tertiary', 'unspecified'])
    .nullable(),
  facility_type_code: string().max(50).nullable().transform(transformEmptyStringToNull),
  claim_frequency_code: string().max(50).nullable().transform(transformEmptyStringToNull),
  status: number().oneOf([1, 2, 3, 4, 19, 20, 21, 22]).nullable(),
  charge_amount: string().max(50).nullable().transform(transformEmptyStringToNull),
  paid_amount: string().max(50).nullable().transform(transformEmptyStringToNull),
  charge_amount_in_cents: number().integer().min(-2147483648).max(2147483647).nullable(),
  paid_amount_in_cents: number().integer().min(-2147483648).max(2147483647).nullable(),
  patient_responsibility_amount_in_cents: number()
    .integer()
    .min(-2147483648)
    .max(2147483647)
    .nullable(),
  claim_filing_indicator_code: string().max(50).nullable().transform(transformEmptyStringToNull),
  payer_claim_control_number: string().max(100).nullable().transform(transformEmptyStringToNull),
  drg_code: string().max(50).nullable().transform(transformEmptyStringToNull),
  claim_received_on: string()
    .nullable()
    .transform(transformEmptyStringToNull)
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Must be a valid date (YYYY-MM-DD)'),
  claim_statement_period_start_on: string()
    .nullable()
    .transform(transformEmptyStringToNull)
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Must be a valid date (YYYY-MM-DD)'),
  claim_statement_period_end_on: string()
    .nullable()
    .transform(transformEmptyStringToNull)
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Must be a valid date (YYYY-MM-DD)'),
  claim_coverage_expiration_on: string()
    .nullable()
    .transform(transformEmptyStringToNull)
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Must be a valid date (YYYY-MM-DD)'),
  claim_contract_code: string().max(50).nullable().transform(transformEmptyStringToNull),
  claim_coverage_amount_in_cents: number().integer().min(-2147483648).max(2147483647).nullable(),
  corrected_insured_last_name: string().max(100).nullable().transform(transformEmptyStringToNull),
  corrected_insured_first_name: string().max(100).nullable().transform(transformEmptyStringToNull),
  corrected_insured_middle_name: string().max(100).nullable().transform(transformEmptyStringToNull),
  corrected_insured_prefix: string().max(50).nullable().transform(transformEmptyStringToNull),
  corrected_insured_suffix: string().max(50).nullable().transform(transformEmptyStringToNull),
  corrected_insured_id_qualifier: string().max(50).nullable(),
  corrected_insured_id: string().max(100).nullable().transform(transformEmptyStringToNull),
  provider_entity_type_qualifier: string().max(50).nullable().transform(transformEmptyStringToNull),
  provider_id_qualifier: string().max(50).nullable().transform(transformEmptyStringToNull),
  provider_id: string().max(100).nullable().transform(transformEmptyStringToNull),
  provider_name: string().max(100).nullable().transform(transformEmptyStringToNull),
  provider_first_name: string().max(100).nullable().transform(transformEmptyStringToNull),
  provider_middle_name: string().max(100).nullable().transform(transformEmptyStringToNull),
  provider_suffix: string().max(50).nullable().transform(transformEmptyStringToNull),
  provider_prefix: string().max(50).nullable().transform(transformEmptyStringToNull),
  financial_info_id: number().nullable(),
  subscriber_first_name: string().max(50).nullable().transform(transformEmptyStringToNull),
  subscriber_id: string().max(50).nullable().transform(transformEmptyStringToNull),
  subscriber_id_qualifier: string().max(50).nullable().transform(transformEmptyStringToNull),
  subscriber_last_name: string().max(50).nullable().transform(transformEmptyStringToNull),
  subscriber_middle_name: string().max(50).nullable().transform(transformEmptyStringToNull),
  subscriber_patient_relationship: string()
    .max(50)
    .nullable()
    .transform(transformEmptyStringToNull),
  subscriber_prefix: string().max(50).nullable().transform(transformEmptyStringToNull),
  subscriber_suffix: string().max(50).nullable().transform(transformEmptyStringToNull),
  patient_first_name: string().max(50).nullable().transform(transformEmptyStringToNull),
  patient_id: string().max(50).nullable().transform(transformEmptyStringToNull),
  patient_id_qualifier: string().max(50).nullable().transform(transformEmptyStringToNull),
  patient_last_name: string().max(50).nullable().transform(transformEmptyStringToNull),
  patient_middle_name: string().max(50).nullable().transform(transformEmptyStringToNull),
  patient_name_prefix: string().max(50).nullable().transform(transformEmptyStringToNull),
  patient_name_suffix: string().max(50).nullable().transform(transformEmptyStringToNull),
  _delete: boolean().optional()
});
